// ApiService.ts
import axios, {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
  AxiosError,
  AxiosHeaders,
} from "axios";
import { BASE_URL } from "../baseUrl"; // 🟢 Grabs the dynamic URL perfectly

class ApiService {
  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // 1. Request Interceptor: Attach Token & Handle FormData
    this.axiosInstance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = localStorage.getItem("jwt_token");
        if (token) {
          if (config.headers) {
            config.headers.set("Authorization", `Bearer ${token}`);
          } else {
            config.headers = new AxiosHeaders({
              Authorization: `Bearer ${token}`,
            });
          }
        }

        // ✅ CRITICAL FIX: Remove Content-Type for FormData
        if (config.data instanceof FormData) {
          config.headers.delete("Content-Type");
        }

        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

    // 🟢 2. Response Interceptor for Global Auto-Logout
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        // If the request succeeds, pass it through normally
        return response;
      },
      (error: AxiosError) => {
        // If the backend rejects the token (Expired or Invalid)
        if (error.response && error.response.status === 401) {
          console.warn("Session expired (401). Executing aggressive auto-logout...");

          // 1. Wipe all local storage immediately
          localStorage.removeItem("jwt_token");
          localStorage.removeItem("user");
          localStorage.removeItem("active_hackathon");
          sessionStorage.clear();

          // 2. Aggressive Redirect: 
          // Using .replace() prevents the user from clicking the "Back" button to return to the broken page
          window.location.replace("/login");

          // 3. 🟢 CRITICAL FIX: Return an empty, unresolved promise!
          // This completely stops the error from reaching your React component.
          // Your UI will not have time to display the JSON error message.
          return new Promise(() => {});
        }

        // If it's a 400, 404, or 500 error, let the component handle it normally
        return Promise.reject(error);
      }
    );
  }

  // Generic GET method
  public async get<T>(
    url: string,
    config?: InternalAxiosRequestConfig,
  ): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.axiosInstance.get<T>(
        url,
        config,
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  // Generic POST method
  public async post<T>(
    url: string,
    data: any,
    config?: InternalAxiosRequestConfig,
  ): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.axiosInstance.post<T>(
        url,
        data,
        config,
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  // Generic PUT method
  public async put<T>(
    url: string,
    data: any,
    config?: InternalAxiosRequestConfig,
  ): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.axiosInstance.put<T>(
        url,
        data,
        config,
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  // Generic DELETE method
  public async delete<T>(
    url: string,
    config?: InternalAxiosRequestConfig,
  ): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.axiosInstance.delete<T>(
        url,
        config,
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  // Error handling
  private handleError(error: any): void {
    console.error("API call failed. Error:", error);
    throw error;
  }
}

export default ApiService;